import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createDistributionList,
  deleteDistributionList,
  fetchDistributionListById,
  fetchDistributionLists,
  updateDistributionList,
} from './DistributionLists.services.ts';
import { toast } from 'react-toastify';
import i18n from '../../utils/i18n.ts';
import type { ApiError } from '../../types/App.ts';
import { useMsal } from '@azure/msal-react';

export const useService = ({ distributionListId }: { distributionListId: number | null }) => {
  const { accounts } = useMsal();
  const queryClient = useQueryClient();

  const hasAccount = accounts.length > 0;
  const createMutation = useMutation({
    mutationFn: createDistributionList,
    onSuccess: () => {
      toast.success(i18n.t('success.api.post-item', { item: i18n.t('module.distribution-list') }));
      queryClient.invalidateQueries({ queryKey: ['distributionLists'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateDistributionList,
    onSuccess: () => {
      toast.success(i18n.t('success.api.put-item', { item: i18n.t('module.distribution-list') }));
      queryClient.invalidateQueries({ queryKey: ['distributionLists'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDistributionList,
    onSuccess: () => {
      toast.success(
        i18n.t('success.api.delete-item', { item: i18n.t('module.distribution-list') }),
      );
      queryClient.invalidateQueries({ queryKey: ['distributionLists'] });
    },
    onError: (error: ApiError) => {
      toast.error(error?.response?.data?.message || error.message);
    },
  });

  const { data: list } = useQuery({
    queryKey: ['distributionList', distributionListId],
    queryFn: () => fetchDistributionListById(distributionListId),
    enabled: !!distributionListId && hasAccount,
    retry: false,
  });

  const { data: lists, isLoading } = useQuery({
    queryKey: ['distributionLists'],
    queryFn: fetchDistributionLists,
    retry: false,
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    list,
    lists,
    isLoading,
  };
};